import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { logError } from 'services/logging/logging';
import { useReprocessMessage } from 'services/repositories/messages/messages';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useRelationship } from 'services/repositories/relationships/relationships';
import { addNotification, NotificationType } from 'stores/notifications/notifications';
import { formatInstantAsDay } from 'support/helpers/dateTime/dateTime';
import { routeToTransactionPage } from 'support/helpers/navigation/navigation';
import { Connectors, MessageV2DTO } from 'support/types';

import { ActionConfig } from '../components/types';
import { ReprocessMessageModal } from './components/ReprocessMessageModal/ReprocessMessageModal';
import { resendAs, resolveBackendErrorSubtitle } from './helpers';

type UseReprocessMessageActionResult = {
  action: ActionConfig;
  isReprocessing: boolean;
};

/**
 * i18n keys
 * t('transactions:show.actionButtons.reprocessConfirmationModal.errorNotIngestible')
 * t('transactions:show.actionButtons.reprocessConfirmationModal.errorForbidden')
 */
const errorSubtitleKeys = {
  422: 'transactions:show.actionButtons.reprocessConfirmationModal.errorNotIngestible',
  403: 'transactions:show.actionButtons.reprocessConfirmationModal.errorForbidden',
};

export const useReprocessMessageAction = (message: MessageV2DTO): UseReprocessMessageActionResult => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: currentPartner } = useCurrentPartner();
  const { data: relationship } = useRelationship({ variables: { relationshipId: message.relationship_id } });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { mutate: reprocessMessage, isLoading: isReprocessing } = useReprocessMessage();

  const senderConnector =
    relationship?.sender_integration?.production?.connector ?? relationship?.sender_integration?.testing?.connector;
  // Only WeClapp/Xentral re-fetch the latest document from the source system on reprocess;
  // every other sender (incl. other ERPs) re-delivers the data we already received.
  const reprocessFetchesLatestSourceData =
    senderConnector === Connectors.WECLAPP || senderConnector === Connectors.XENTRAL_V3;

  const handleReprocess = () => {
    setIsModalOpen(false);
    reprocessMessage(
      { messageId: message.id },
      {
        onSuccess: ({ transactionId, transactionType }) => {
          addNotification(
            { title: t('transactions:show.actionButtons.reprocessConfirmationModal.successNotification') },
            NotificationType.success,
          );
          navigate(routeToTransactionPage(transactionType, transactionId));
        },
        onError: (error) => {
          logError(error);
          addNotification(
            {
              title: t('transactions:show.actionButtons.reprocessConfirmationModal.errorNotification'),
              subtitle: resolveBackendErrorSubtitle(error, t, errorSubtitleKeys),
            },
            NotificationType.error,
          );
        },
      },
    );
  };

  /**
   * i18n keys
   * t('transactions:show.actionButtons.reprocess.label.latestData')
   * t('transactions:show.actionButtons.reprocess.label.received')
   */
  const labelSuffix = reprocessFetchesLatestSourceData ? 'latestData' : 'received';

  const shouldBeDisabled = (msg: MessageV2DTO) => !msg.can_resend;
  return {
    action: {
      label: `transactions:show.actionButtons.reprocess.label.${labelSuffix}`,
      onClick: () => setIsModalOpen(true),
      analyticsId: 'transactions:reprocess_message',
      group: 'resend',
      secondaryOptionOnly: true,
      test: (msg) => resendAs(msg, currentPartner?.id) === 'sender',
      disabled: shouldBeDisabled,
      description: (msg) =>
        shouldBeDisabled(msg) ? t('transactions:show.actionButtons.reprocess.description') : undefined,
      renderModal: () => (
        <ReprocessMessageModal
          open={isModalOpen}
          setOpen={setIsModalOpen}
          onConfirm={handleReprocess}
          fetchesLatestSourceData={reprocessFetchesLatestSourceData}
          documentDate={formatInstantAsDay(message.created_at)}
        />
      ),
    },
    isReprocessing,
  };
};
